import type { User } from "@supabase/supabase-js";
import {
  cacheValidSubscription,
  selectActiveSubscription,
} from "@/lib/billing/subscriptionCache";
import type {
  SessionEntitlements,
  SessionSubscription,
} from "@/lib/auth/sessionTypes";
import { store } from "@/lib/states/store";
import { trpcUtils } from "@/lib/trpc/vanilla";

type PersistApi = {
  hasHydrated: () => boolean;
  onFinishHydration: (callback: () => void) => () => void;
};

let refreshInFlight: Promise<void> | null = null;

export function waitForStoreHydration(): Promise<void> {
  const persist = (
    store as unknown as { persist?: PersistApi }
  ).persist;
  if (!persist || persist.hasHydrated()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    persist.onFinishHydration(() => resolve());
  });
}

export function hasUsablePersistedSession(userId: string): boolean {
  const session = store.getState().session;
  return Boolean(
    session.initialised &&
      session.userId === userId &&
      session.org?.id &&
      session.org.setupComplete,
  );
}

export function seedBillingQueryCachesFromSession() {
  const session = store.getState().session;
  const orgId = session.org?.id ?? session.activeOrgId ?? null;
  if (orgId && session.subscription) {
    cacheValidSubscription(orgId, session.subscription);
  }
  if (session.entitlements) {
    trpcUtils.billing.entitlements.setData(
      undefined,
      session.entitlements as never,
    );
  }
}

export function applyAuthBootstrapCaches(bootstrap: {
  subscriptions?: SessionSubscription[] | null;
  entitlements?: SessionEntitlements | null;
  selected?: { orgId: string } | null;
}) {
  const orgId = bootstrap.selected?.orgId ?? null;
  const existing = store.getState().session;

  if (bootstrap.entitlements) {
    trpcUtils.billing.entitlements.setData(
      undefined,
      bootstrap.entitlements as never,
    );
  }

  // Missing subscriptions means the API did not send billing in this payload.
  // Keep whatever we already have instead of treating it as "no plan".
  if (bootstrap.subscriptions == null) {
    return {
      subscription: existing.subscription,
      entitlements: bootstrap.entitlements ?? existing.entitlements ?? null,
    };
  }

  const subscriptions = bootstrap.subscriptions;
  const subscription = selectActiveSubscription(subscriptions);

  if (subscriptions.length > 0) {
    trpcUtils.billing.subscription.setData(undefined, subscriptions as never);
  }
  if (orgId) {
    cacheValidSubscription(orgId, subscription);
  }

  return {
    subscription,
    entitlements: bootstrap.entitlements ?? existing.entitlements ?? null,
  };
}

function startBackgroundSessionRefresh(user: User) {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = store
    .getState()
    .session.initWithUserAsync(user, { force: true })
    .catch((error: unknown) => {
      console.error("[session] Background refresh failed", error);
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

export async function ensureAuthenticatedSession(
  user: User,
  options?: { staleOk?: boolean },
) {
  await waitForStoreHydration();

  if (options?.staleOk && hasUsablePersistedSession(user.id)) {
    seedBillingQueryCachesFromSession();
    if (store.getState().session.needsRefreshAfterRehydrate) {
      void startBackgroundSessionRefresh(user);
    }
    return;
  }

  await store.getState().session.initWithUserAsync(user, {
    force: true,
  });
}
